import { FC } from 'react';
import Modal from 'components/shared/Modal';
import { Alert, Button } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import { ListPost } from 'types/PostDetail';
import { useMutation, useQueryClient } from 'react-query';
import PostApi from 'api/post';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import { useToast } from 'app/hooks/useToast';
import { useG2ReviewTrigger } from 'app/hooks/useG2ReviewTrigger';
import HorizontalPostCard from 'app/modules/posts/components/HorizontalPostCard';

type Props = {
  initialPosts: ListPost[];
  onFinish: () => void;
};

const MovePostsToTrashPreselectedPosts: FC<Props> = ({
  initialPosts,
  onFinish,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { triggerG2Review } = useG2ReviewTrigger();

  const moveToTrash = useMutation(
    (postIds: number[]) => PostApi.moveMultipleToTrash(postIds),
    {
      onError: (e: ApiClientError) => {
        toast(
          e?.userMessage ??
            t('unableToMovePostsToTrash', 'Unable to move posts to trash'),
          'error',
        );
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries(queryKey.trashedPosts());
        queryClient.invalidateQueries(queryKey.trashedPostsCount());
        onFinish();

        if (data.movedCount > 0) {
          triggerG2Review();
          toast(
            t('postsMovedToTrash', {
              defaultValue_one: '{{count}} post moved to trash',
              defaultValue_other: '{{count}} posts moved to trash',
              defaultValue: '{{count}} posts moved to trash',
              count: data.movedCount,
            }),
            'info',
          );
        }

        if (data.failedCount > 0) {
          toast(
            t('somePostsFailedToMoveToTrash', {
              defaultValue_one: '{{count}} post could not be moved to trash',
              defaultValue_other: '{{count}} posts could not be moved to trash',
              defaultValue: '{{count}} posts could not be moved to trash',
              count: data.failedCount,
            }),
            'warning',
          );
        }
      },
    },
  );

  const handleSubmit = () => {
    const postIds = initialPosts.map((post) => post.id);
    moveToTrash.mutate(postIds);
  };

  return (
    <>
      <Modal.Header title={t('moveToTrash', 'Move to trash')} />
      <Modal.Content>
        <Alert variant="warning" className="tw-mb-4">
          {t(
            'movePostsToTrashWarning',
            'Posts will be moved to trash. You can restore them later from the trash.',
          )}
        </Alert>
        <p className="tw-mb-4 tw-text-md tw-text-grayscale-140">
          {t('movePostsToTrashConfirm', {
            defaultValue_one:
              'Are you sure you want to move {{count}} post to trash?',
            defaultValue_other:
              'Are you sure you want to move {{count}} posts to trash?',
            defaultValue:
              'Are you sure you want to move {{count}} posts to trash?',
            count: initialPosts.length,
          })}
        </p>
        <div className="tw-max-h-[300px] tw-overflow-y-auto tw-rounded-md tw-border tw-border-grayscale-20">
          {initialPosts.map((post) => (
            <HorizontalPostCard key={post.id} post={post} withPageInfo />
          ))}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button variant="secondary" onClick={onFinish}>
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isLoading={moveToTrash.isLoading}
        >
          {t('moveToTrash', 'Move to trash')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default MovePostsToTrashPreselectedPosts;
